import React, { memo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { TextInputControl } from 'components/_basic/Form/TextInput';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload';

import { concatWithDot } from 'helpers/string';

import {
  COMPETITIVE_ANALYSIS_MAX_LENGTHS,
  COMPETITORS_COMPARISON_NAMES,
  FEATURES_FOR_COMPARISON_NAMES,
  FEATURES_LIST_NAMES,
  IFeaturesForComparison,
} from '../utils/form';

export const FeaturesList: React.FC<{ name: string; namePrefix?: string; disabled?: boolean }> = memo(
  ({ name, disabled, namePrefix = '' }) => {
    const { t } = useTranslation();
    const { control } = useFormContext();

    const fieldName = namePrefix ? namePrefix : name;

    const features = useWatch({
      control,
      name: concatWithDot(name, COMPETITORS_COMPARISON_NAMES.FEATURES_FOR_COMPARISON),
    })
      .map((item: IFeaturesForComparison) => item[FEATURES_FOR_COMPARISON_NAMES.FEATURE])
      .filter(Boolean);

    return (
      <Grid container spacing={2}>
        <Grid item xs="auto">
          <TooltipFormImageUpload
            width={64}
            height={64}
            placeholder="Logo"
            name={concatWithDot(fieldName, FEATURES_LIST_NAMES.FILE)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextInputControl
                control={control}
                id={concatWithDot(fieldName, FEATURES_LIST_NAMES.NAME)}
                name={concatWithDot(fieldName, FEATURES_LIST_NAMES.NAME)}
                label={t(
                  namePrefix
                    ? 'marketAnalysisForm.competitors.competitorName'
                    : 'marketAnalysisForm.competitors.yourCompanyName',
                )}
                disabled={disabled}
                maxLength={42}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {features.map((item: string, index: number) => {
                  return (
                    <Grid item xs={6} key={item + index}>
                      <TextInputControl
                        control={control}
                        id={concatWithDot(fieldName, `${FEATURES_LIST_NAMES.FEATURE}.${index}`)}
                        name={concatWithDot(fieldName, `${FEATURES_LIST_NAMES.FEATURE}.${index}`)}
                        label={item}
                        disabled={disabled}
                        maxLength={COMPETITIVE_ANALYSIS_MAX_LENGTHS.FEATURE_LIST.FEATURE_VALUE}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  },
);
